// src/Dashboard.js - VERSIONE CON DRAG & DROP

import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { useUser } from './UserContext';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import './Dashboard.css';
import { WIDGET_REGISTRY } from './DashboardWidgets';
import { FaPlus, FaTimes, FaCog } from 'react-icons/fa';
import { toast } from 'react-toastify';

function Dashboard() {
    const { userProfile } = useUser();
    const [activeWidgets, setActiveWidgets] = useState([]); 
    const [isEditMode, setIsEditMode] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [loadingLayout, setLoadingLayout] = useState(true);
    
    // Stato per gestire l'elemento trascinato
    const [draggedItemIndex, setDraggedItemIndex] = useState(null);

    // 1. Carica il layout
    useEffect(() => {
        if (!userProfile?.centerId) return;

        const fetchLayout = async () => {
            const userDocRef = doc(db, 'centers', userProfile.centerId, 'staff', userProfile.uid);
            const snap = await getDoc(userDocRef);
            
            if (snap.exists() && snap.data().dashboardLayout && snap.data().dashboardLayout.length > 0) {
                setActiveWidgets(snap.data().dashboardLayout);
            } else {
                // 🎯 FIX CHIRURGICO: Setup predefinito in base al ruolo
                if (userProfile?.isDoctorProfile) {
                    // Pre-carichiamo i 3 widget specifici per il medico
                    setActiveWidgets(['doctor_planning', 'doctor_compensations', 'doctor_stats']);
                } else {
                    setActiveWidgets([]); 
                }
            }
            setLoadingLayout(false);
        };
        fetchLayout();
    }, [userProfile]);

    // 2. Salva il layout
    const saveLayout = async (newLayout) => {
        setActiveWidgets(newLayout);
        try {
            const userDocRef = doc(db, 'centers', userProfile.centerId, 'staff', userProfile.uid);
            await updateDoc(userDocRef, { dashboardLayout: newLayout });
            // toast.success("Layout salvato!"); // Opzionale, magari troppo invasivo
        } catch (error) {
            console.error("Errore salvataggio layout:", error);
            toast.error("Errore salvataggio dashboard.");
        }
    };

    // 3. Aggiungi Widget
    const handleAddWidget = (widgetKey) => {
        if (!activeWidgets.includes(widgetKey)) {
            saveLayout([...activeWidgets, widgetKey]);
        }
        setIsAddModalOpen(false);
    };

    // 4. Rimuovi Widget
    const handleRemoveWidget = (widgetKey) => {
        const newLayout = activeWidgets.filter(k => k !== widgetKey);
        saveLayout(newLayout);
    };

    // --- FUNZIONI DRAG & DROP ---
    
    const handleDragStart = (e, index) => {
        setDraggedItemIndex(index);
        // Effetto visivo per il trascinamento (necessario per Firefox)
        e.dataTransfer.effectAllowed = "move";
        // Opacità ridotta sull'elemento fantasma gestita dal browser
    };

    const handleDragOver = (e, index) => {
        e.preventDefault(); // Necessario per permettere il drop
        e.dataTransfer.dropEffect = "move";
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        
        // Se stiamo rilasciando sullo stesso elemento, non fare nulla
        if (draggedItemIndex === null || draggedItemIndex === dropIndex) return;

        const newLayout = [...activeWidgets];
        // Rimuovi l'elemento dalla vecchia posizione
        const [draggedItem] = newLayout.splice(draggedItemIndex, 1);
        // Inseriscilo nella nuova posizione
        newLayout.splice(dropIndex, 0, draggedItem);

        saveLayout(newLayout);
        setDraggedItemIndex(null);
    };

    const handleDragEnd = () => {
        setDraggedItemIndex(null);
    };

    // Filtra i widget disponibili
    const availableWidgets = Object.entries(WIDGET_REGISTRY).filter(([key, config]) => {
        return config.checkPermission(userProfile.permissions || {}, userProfile); // 🎯 FIX: Passato userProfile
    });

    if (loadingLayout) return <div className="loading-text">Caricamento Dashboard...</div>;

    return (
        <div className="dashboard-container">
            <div className="dashboard-header-row" style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px'}}>
                <h1>{userProfile.name}</h1>
                <div style={{display:'flex', gap:'10px'}}>
                    <button 
                        className={`customize-btn ${isEditMode ? 'active' : ''}`} 
                        onClick={() => setIsEditMode(!isEditMode)}
                        title="Modifica Dashboard"
                    >
                        <FaCog /> {isEditMode ? 'Fine Modifica' : 'Personalizza'}
                    </button>
                    
                    {(isEditMode || activeWidgets.length === 0) && (
                        <button className="add-widget-btn-header" onClick={() => setIsAddModalOpen(true)}>
                            <FaPlus /> Aggiungi Card
                        </button>
                    )}
                </div>
            </div>

            {activeWidgets.length === 0 && !isAddModalOpen ? (
                <div className="empty-dashboard-state">
                    <h3>👋 Benvenuto nella tua nuova Dashboard!</h3>
                    <p>Attualmente è vuota. Aggiungi le card che ti servono per iniziare.</p>
                    <button className="big-add-btn" onClick={() => setIsAddModalOpen(true)}>+ Aggiungi Widget</button>
                </div>
            ) : (
                <div className="dashboard-grid">
                    {activeWidgets.map((key, index) => {
                        const widgetConfig = WIDGET_REGISTRY[key];
                        if (!widgetConfig) return null; 

                        if (!widgetConfig.checkPermission(userProfile.permissions || {}, userProfile)) return null; // 🎯 FIX: Passato userProfile

                        const WidgetComponent = widgetConfig.component;
                        const isFullWidth = widgetConfig.defaultSize === '2';
                        const isDragging = draggedItemIndex === index;

                        return (
                            <div 
                                key={key} 
                                className={`widget-wrapper ${isFullWidth ? 'full-width' : ''} ${isEditMode ? 'edit-mode' : ''} ${isDragging ? 'dragging' : ''}`}
                                // Attributi Drag & Drop (attivi solo in Edit Mode)
                                draggable={isEditMode}
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDrop={(e) => handleDrop(e, index)}
                                onDragEnd={handleDragEnd}
                            >
                                {isEditMode && (
                                    <div className="widget-overlay-controls">
                                        <span className="drag-handle">::: Sposta</span>
                                        <button className="remove-widget-btn" onClick={() => handleRemoveWidget(key)}>
                                            <FaTimes />
                                        </button>
                                    </div>
                                )}
                                <div style={{pointerEvents: isEditMode ? 'none' : 'auto'}}>
                                     <WidgetComponent />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {isAddModalOpen && (
                <div className="modal" onMouseDown={() => setIsAddModalOpen(false)}>
                    <div className="modal-content modal-content-large" onMouseDown={e => e.stopPropagation()}>
                        <h3>Aggiungi alla Dashboard</h3>
                        <div className="widgets-selection-grid">
                            {availableWidgets.map(([key, config]) => {
                                const isAdded = activeWidgets.includes(key);
                                return (
                                    <button 
                                        key={key} 
                                        className={`widget-select-card ${isAdded ? 'added' : ''}`}
                                        onClick={() => handleAddWidget(key)}
                                        disabled={isAdded}
                                    >
                                        <span className="widget-title">{config.title}</span>
                                        {isAdded && <span className="added-badge">Aggiunto</span>}
                                    </button>
                                );
                            })}
                        </div>
                        <div style={{marginTop: '20px', textAlign: 'right'}}>
                            <button className="modal-button neutral-button" onClick={() => setIsAddModalOpen(false)}>Chiudi</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Dashboard;